import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { BackgroundGlow } from "~/components/background-glow";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="relative min-h-screen">
      <BackgroundGlow />

      <div className="relative mx-auto max-w-4xl px-6 py-16">
        <div className="mb-10">
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Overseer – Terms of Service
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Last updated: 26 May 2026
          </p>
        </div>

        <div className="mb-10 space-y-6 rounded-2xl border border-border bg-card/30 p-6 shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150 md:p-8">
            <p className="text-sm leading-relaxed text-muted-foreground">
              These Terms of Service (“Terms”) govern your access to and use of
              Overseer (the “Service”), including our Discord bot, website, and
              subscription system.
            </p>

            <p className="text-sm leading-relaxed text-muted-foreground">
              By using Overseer, you agree to these Terms. If you do not agree,
              you must not use the Service.
            </p>
        </div>

        <div className="space-y-6">
          <Section title="1. Who We Are">
            <p>
              Overseer is an independently developed software project operated
              by private individuals based in the United Kingdom.
            </p>
            <p>
              We are not a registered company. References to “we”, “us”, or
              “our” refer to the operators of Overseer.
            </p>
            <p>
              Overseer is not affiliated with, endorsed by, or officially
              connected to:
            </p>
            <ul className="list-disc pl-6">
              <li>Studio Wildcard</li>
              <li>Nitrado</li>
              <li>Discord</li>
              <li>Microsoft</li>
            </ul>
            <p className="mt-2">
              All trademarks are the property of their respective owners.
            </p>
          </Section>

          <Section title="2. Description of the Service">
            <p>
              Overseer is a management tool for <strong>ARK: Survival
              Evolved (Xbox)</strong> servers hosted through{" "}
              <strong>Nitrado</strong>.
            </p>
            <p>It provides:</p>
            <ul className="list-disc pl-6">
              <li>Discord-based server management tools</li>
              <li>Player monitoring and event logging</li>
              <li>Tribe and character tracking</li>
              <li>Ban management</li>
              <li>Administrative permission controls</li>
              <li>Optional subscription features</li>
            </ul>
            <p className="mt-2">
              The Service integrates with the Discord API and the Nitrado API.
              Your use of Overseer is also subject to the terms and policies
              of those providers.
            </p>
          </Section>

          <Section title="3. Eligibility">
            <p>You must:</p>
            <ul className="list-disc pl-6">
              <li>
                Be at least 13 years old (or the minimum age required
                by Discord in your country).
              </li>
              <li>Have the legal authority to agree to these Terms.</li>
              <li>
                Use the Service in compliance with all applicable
                laws and platform rules.
              </li>
            </ul>

            <p className="mt-4">
              If you use Overseer on behalf of a Discord server, you confirm
              that you have permission to install and operate the bot.
            </p>

            <h3 className="mt-6 text-base font-semibold text-foreground">
              Subscription Age Requirement
            </h3>
            <p>
              To purchase a subscription, you must be at least 18 years old,
              or have the permission of a parent or legal guardian.
            </p>
            <p>
              By purchasing a subscription, you confirm that you meet this
              requirement.
            </p>
          </Section>

          <Section title="4. Data We Process">
            <p>
              Overseer stores and processes data necessary to provide server
              management features, including:
            </p>
            <ul className="list-disc pl-6">
              <li>Discord server IDs and user IDs</li>
              <li>Gamertags and platform identifiers</li>
              <li>Tribe data</li>
              <li>Character data</li>
              <li>Player event logs</li>
              <li>Ban records</li>
              <li>Administrative permissions</li>
              <li>Subscription and payment references</li>
            </ul>
            <p>
              Sensitive tokens (such as API tokens) are encrypted before
              storage.
            </p>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6">
              <li>Use Overseer for unlawful purposes</li>
              <li>
                Attempt to gain unauthorised access to the Service or its
                infrastructure
              </li>
              <li>Interfere with Discord or Nitrado services</li>
              <li>Reverse engineer or exploit vulnerabilities</li>
              <li>
                Use the Service to harass or unlawfully target individuals
              </li>
              <li>
                Attempt to bypass subscription or payment systems
              </li>
            </ul>
            <p className="mt-2">
              We reserve the right to suspend or terminate access for
              violations of these Terms.
            </p>
          </Section>

          <Section title="6. Subscriptions and Payments">
            <p>Certain features require a paid subscription.</p>
            <ul className="list-disc pl-6">
              <li>You agree to pay all applicable fees.</li>
              <li>
                You authorise payment processing through our third-party
                payment provider.
              </li>
              <li>
                Subscriptions may auto-renew unless cancelled.
              </li>
            </ul>
            <p className="mt-2">
              Refunds are provided at our discretion unless required by UK
              consumer law.
            </p>
          </Section>

          <Section title="7. Service Availability">
            <p>We aim to keep Overseer available and reliable. However:</p>
            <ul className="list-disc pl-6">
              <li>We do not guarantee uninterrupted operation.</li>
              <li>
                We are not responsible for outages caused by Discord,
                Nitrado, hosting providers, or infrastructure outside our
                control.
              </li>
              <li>
                Third-party service degradation may impact performance or
                features.
              </li>
              <li>
                Features may change, be removed, or updated without notice.
              </li>
            </ul>
            <p className="mt-2">
              The Service is provided on an “as is” and “as available” basis.
            </p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p>To the fullest extent permitted by UK law:</p>
            <ul className="list-disc pl-6">
              <li>
                We are not liable for indirect, incidental, or consequential
                damages.
              </li>
              <li>
                We are not liable for loss of profits, data, reputation, or
                server access.
              </li>
              <li>
                We are not responsible for actions taken by server
                administrators using Overseer.
              </li>
            </ul>
            <p className="mt-2">
              Nothing in these Terms excludes liability that cannot legally
              be excluded under UK law.
            </p>
          </Section>

          <Section title="9. Termination">
            <p>
              We may suspend or terminate access to the Service if you breach
              these Terms, if required by law, or if the Service is
              discontinued.
            </p>
            <p>
              You may stop using the Service at any time by removing the bot
              and discontinuing subscriptions.
            </p>
          </Section>

          <Section title="10. Changes to These Terms">
            <p>
              We may update these Terms from time to time. Continued use of
              the Service constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="11. Governing Law">
            <p>
              These Terms are governed by the laws of England and Scotland.
            </p>
            <p>
              Any disputes shall be subject to the jurisdiction of the courts
              of England and Scotland.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              For questions regarding these Terms, please contact us through
              our official Discord server.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card/30 p-6 shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150 md:p-8">
      <h2 className="mb-4 text-lg font-bold tracking-tight text-foreground">
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground [&_strong]:font-semibold [&_strong]:text-foreground [&_ul]:space-y-1.5 [&_ul]:pl-6 [&_ul]:marker:text-primary">
        {children}
      </div>
    </section>
  );
}